import { runInAction, makeObservable, observable, action } from 'mobx';
import BaseViewModel from './BaseViewModel';
import Segmentation from '../models/Segmentation';
import SegmentationCriterion from '../models/SegmentationCriterion';
import Criterion from '../models/Criterion';

export enum SegmentationConfigurationBusyStateKey {
  FETCH_AVAILABLE_CRITERIA = 'fetchAvailableCriteria',
  FETCH_SEGMENTATION = 'fetchSegmentation',
  ADD_CRITERION = 'addCriterion',
  CREATE_CRITERION = 'createCriterion',
  CREATE_CRITERION_ONLY = 'createCriterionOnly',
  DELETE_CRITERION = 'deleteCriterion',
}

export default class SegmentationConfigurationViewModel extends BaseViewModel<SegmentationConfigurationBusyStateKey> {
  availableCriteria: Criterion[] = [];
  currentSegmentation?: Segmentation = undefined;

  constructor() {
    super();

    Object.values(SegmentationConfigurationBusyStateKey).forEach(key => {
      this.isLoading[key] = false;
    });

    makeObservable(this, {
      availableCriteria: observable,
      currentSegmentation: observable,
      fetchData: action,
      fetchSegmentation: action,
      addCriterion: action,
      createCriterion: action,
      deleteCriterion: action
    });
    this.fetchData();
  }

  async fetchData() {
    runInAction(() => {
      this.setBusy(SegmentationConfigurationBusyStateKey.FETCH_AVAILABLE_CRITERIA, true);
      this.error = null;
    });
    try {
      const availableCriteriaResponse = await this.api.get<Criterion[]>('/criteria');
      runInAction(() => {
        this.availableCriteria = availableCriteriaResponse.data;
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to fetch attributes. Please try again later.';
      });
    } finally {
      runInAction(() => {
        this.setBusy(SegmentationConfigurationBusyStateKey.FETCH_AVAILABLE_CRITERIA, false);
      });
    }
  }

  async fetchSegmentation(id: number) {
    runInAction(() => {
      this.setBusy(SegmentationConfigurationBusyStateKey.FETCH_SEGMENTATION, true);
      this.error = null;
    });
    try {
      const response = await this.api.get<Segmentation>(`/segmentations/${id}`);
      runInAction(() => {
        this.currentSegmentation = response.data;
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to fetch segmentation. Please try again later.';
      });
    } finally {
      runInAction(() => {
        this.setBusy(SegmentationConfigurationBusyStateKey.FETCH_SEGMENTATION, false);
      });
    }
  }

  async addCriterion(id: string, priority: number, segmentationId: number) {
    runInAction(() => {
      this.setBusy(SegmentationConfigurationBusyStateKey.ADD_CRITERION, true);
      this.error = null;
    });
    try {
      const response = await this.api.post<SegmentationCriterion>(`/segmentation_criterion`, { criterion_id: Number(id), priority, segmentation_id: segmentationId });
      runInAction(() => {
        if (this.currentSegmentation) {
          this.currentSegmentation = {
            ...this.currentSegmentation,
            segmentation_criteria: [...(this.currentSegmentation.segmentation_criteria || []), response.data]
          };
        }
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to add criterion. Please try again later.';
      });
    } finally {
      runInAction(() => {
        this.setBusy(SegmentationConfigurationBusyStateKey.ADD_CRITERION, false);
      });
    }
  }

  async deleteCriterion(segmentationCriterion: SegmentationCriterion) {
    if (!this.currentSegmentation) {
      return;
    }
    runInAction(() => {
      this.setBusy(SegmentationConfigurationBusyStateKey.DELETE_CRITERION, true);
      this.error = null;
    });
    try {
      await this.api.delete(`/segmentation_criterion/${segmentationCriterion.id}`);
      runInAction(() => {
        if (this.currentSegmentation) {
          this.currentSegmentation = {
            ...this.currentSegmentation,
            segmentation_criteria: this.currentSegmentation.segmentation_criteria?.filter(
              c => c.criterion.id !== segmentationCriterion.criterion.id
            ) || []
          };
        }
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to delete criterion. Please try again later.';
      });
    } finally {
      runInAction(() => {
        this.setBusy(SegmentationConfigurationBusyStateKey.DELETE_CRITERION, false);
      });
    }
  }

  async createCriterion(name: string, prompt: string, priority: number, segmentationId: number) {
    runInAction(() => {
      this.setBusy(SegmentationConfigurationBusyStateKey.CREATE_CRITERION, true);
      this.error = null;
    });
    try {
      const createResponse = await this.api.post<Criterion>('/criteria', { name, prompt });
      const addResponse = await this.api.post<SegmentationCriterion>(`/segmentation_criterion`, { criterion_id: createResponse.data.id, priority, segmentation_id: segmentationId });
      runInAction(() => {
        if (this.currentSegmentation) {
          this.currentSegmentation = {
            ...this.currentSegmentation,
            segmentation_criteria: [...(this.currentSegmentation.segmentation_criteria || []), addResponse.data]
          };
        }
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to create criterion. Please try again later.';
      });
    } finally {
      runInAction(() => {
        this.setBusy(SegmentationConfigurationBusyStateKey.CREATE_CRITERION, false);
      });
    }
  }

  async createCriterionOnly(name: string, prompt: string) {
    runInAction(() => {
      this.setBusy(SegmentationConfigurationBusyStateKey.CREATE_CRITERION_ONLY, true);
      this.error = null;
    });
    try {
      const response = await this.api.post<Criterion>('/criteria', { name, prompt });
      runInAction(() => {
        this.availableCriteria.push(response.data);
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to create criterion. Please try again later.';
      });
    } finally {
      runInAction(() => {
        this.setBusy(SegmentationConfigurationBusyStateKey.CREATE_CRITERION_ONLY, false);
      });
    }
  }
}